import { Navigate, Route, Routes } from 'react-router-dom';
import { ADMIN_ONLY, PORTAL_ROLES, RequireRole } from './routes/RequireRole.js';
import { useFig } from './session/FigProvider.js';
import { FullPageSpinner } from './components/FullPageSpinner.js';
import { SignIn } from './routes/SignIn.js';
import { Handoff } from './routes/Handoff.js';
import { PortalRoutes } from './routes/portal/PortalRoutes.js';
import { AdminHome } from './routes/admin/AdminHome.js';

/**
 * Two trees, one app, two different tokens.
 *
 * `/handoff` arriving from the app's "Open the portal", already signed in (no guard: it signs you in)
 * `/`        decides which tree a person belongs in, from their role - nobody is ever asked
 * `/calls`   Client Portal
 * `/admin`   Agency administration (Agency Admin only)
 *
 * Every branch is wrapped in RequireRole. There is no unguarded route below `/sign-in`.
 *
 * **Both guards fall back to `/`, never to each other.** Pointing them at one another looks
 * tidier and produces an INFINITE REDIRECT LOOP for anyone allowed in neither tree: an
 * `individual` or `qareviewer` at /admin bounces to /calls, which bounces back to /admin, for
 * ever. Their browser hangs. `/` is the only terminal destination, because RoleLanding always
 * resolves to a real screen. Caught by boundary.test.tsx, which is why it is written this way.
 */
export default function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/handoff" element={<Handoff />} />
      <Route path="/" element={<RoleLanding />} />

      <Route
        path="/calls/*"
        element={
          <RequireRole allow={PORTAL_ROLES}>
            <PortalRoutes />
          </RequireRole>
        }
      />

      <Route
        path="/admin/*"
        element={
          <RequireRole allow={ADMIN_ONLY}>
            <AdminHome />
          </RequireRole>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * The role decides the destination. This is the pebble rule applied to routing: a person who
 * has just signed in should land in their product, not on a menu asking which product they are.
 */
function RoleLanding() {
  const { role, ready } = useFig();
  if (!ready) return <FullPageSpinner label="Signing you in" />;
  if (role === null) return <Navigate to="/sign-in" replace />;
  if (role === 'client_portal') return <Navigate to="/calls" replace />;
  if (role === 'admin') return <Navigate to="/admin" replace />;
  // An agency user who is not an admin has no surface in THIS app - administration is
  // admin-only and the portal is not theirs. The native app is where they belong.
  return <Navigate to="/sign-in?wrong-app=1" replace />;
}
